import { useEffect, useMemo, useState, type ReactElement } from "react";

import { createBrowserAccountApi, type AccountCustomer } from "./account-api";
import { createBrowserAccountFilesApi, type AccountArtworkFile } from "./account-files-api";
import { createBrowserCartApi, type StorefrontCart } from "./cart-api";
import { AccountChrome } from "./site-chrome";

export const ACCOUNT_FILES_PATH = "/account/files";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat("he-IL").format(parsed);
}

export function AccountFilesLauncher(): ReactElement | null {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path !== "/account") return null;
  return <a className="account-files-launcher" href={ACCOUNT_FILES_PATH}>הקבצים שלי</a>;
}

export function AccountFilesPage(): ReactElement {
  const accountApi = useMemo(() => createBrowserAccountApi(), []);
  const filesApi = useMemo(() => createBrowserAccountFilesApi(), []);
  const cartApi = useMemo(() => createBrowserCartApi(), []);
  const [customer, setCustomer] = useState<AccountCustomer | null | undefined>(undefined);
  const [files, setFiles] = useState<AccountArtworkFile[] | undefined>(undefined);
  const [cart, setCart] = useState<StorefrontCart | null | undefined>(undefined);
  const [targetLineId, setTargetLineId] = useState("");
  const [reusingFileId, setReusingFileId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      accountApi.loadCustomer(controller.signal),
      filesApi.load(controller.signal),
      cartApi.load(),
    ]).then(([nextCustomer, nextFiles, nextCart]) => {
      setCustomer(nextCustomer);
      setFiles(nextFiles);
      setCart(nextCart);
      setTargetLineId(nextCart?.lines[0]?.id ?? "");
    }).catch(() => {
      setError("לא הצלחנו לטעון את ספריית הקבצים כרגע.");
      setCustomer(null);
      setFiles([]);
      setCart(null);
    });
    return () => controller.abort();
  }, [accountApi, cartApi, filesApi]);

  const reuse = async (file: AccountArtworkFile) => {
    if (!targetLineId || reusingFileId) return;
    setReusingFileId(file.id);
    setMessage(null);
    setError(null);
    try {
      await filesApi.reuse(file.id, targetLineId);
      setMessage(`הקובץ “${file.originalFilename}” שויך לפריט שבחרת בסל.`);
    } catch {
      setError("לא הצלחנו לשייך את הקובץ לפריט בסל. רעננו את הסל ונסו שוב.");
    } finally {
      setReusingFileId(null);
    }
  };

  if (customer === undefined || files === undefined || cart === undefined) {
    return <AccountChrome><main className="account-status-page">טוענים את הקבצים שלך…</main></AccountChrome>;
  }

  if (!customer) {
    return (
      <AccountChrome>
        <main className="account-status-page">
          <section>
            <h1>צריך להתחבר לחשבון</h1>
            <p>ספריית הקבצים זמינה רק לבעל החשבון.</p>
            <a className="account-primary-button account-button-link" href="/login">לכניסה</a>
          </section>
        </main>
      </AccountChrome>
    );
  }

  return (
    <AccountChrome>
      <main className="account-page account-files-page">
        <div className="account-files-heading">
          <div>
            <span className="account-kicker">האזור האישי</span>
            <h1>הקבצים שלי</h1>
            <p>קבצי עיצוב שהעלית להזמנות קודמות. אפשר להוריד אותם או לשייך אותם מחדש לפריט שנמצא כרגע בסל.</p>
          </div>
          <a className="account-secondary-button" href="/account">חזרה להזמנות</a>
        </div>

        {message ? <div className="account-success" role="status">{message}</div> : null}
        {error ? <div className="account-error" role="alert">{error}</div> : null}

        <section className="account-panel account-files-reuse-panel">
          <div>
            <span className="account-kicker">שימוש חוזר</span>
            <h2>לאיזה פריט בסל לשייך את הקובץ?</h2>
            <p>השימוש החוזר הוא פעולה מפורשת. המערכת לא מעתיקה קבצים אוטומטית בהזמנה חוזרת.</p>
          </div>
          {cart?.lines.length ? (
            <label className="account-files-target">
              <span>פריט יעד בסל</span>
              <select value={targetLineId} onChange={event => setTargetLineId(event.target.value)}>
                {cart.lines.map((line, index) => (
                  <option key={line.id} value={line.id}>{index + 1}. {line.productName} — כמות {line.quantity}</option>
                ))}
              </select>
            </label>
          ) : (
            <div className="account-files-no-cart">
              <strong>אין כרגע פריט בסל.</strong>
              <span>כדי להשתמש בקובץ שוב, הוסיפו קודם מוצר לסל.</span>
              <a href="/pvc-sign">להוספת שלט PVC</a>
            </div>
          )}
        </section>

        <section className="account-panel">
          <div className="account-panel-heading">
            <div><span className="account-kicker">קבצים מהזמנות קודמות</span><h2>ספריית עיצובים</h2></div>
            <span>{files.length} קבצים</span>
          </div>

          {files.length === 0 ? (
            <div className="account-empty-list">
              <p>עדיין אין קבצי עיצוב שמורים מהזמנות קודמות.</p>
            </div>
          ) : (
            <div className="account-files-grid">
              {files.map(file => (
                <article className="account-file-card" key={file.id}>
                  <div className="account-file-icon" aria-hidden="true">↧</div>
                  <div className="account-file-main">
                    <strong title={file.originalFilename}>{file.originalFilename}</strong>
                    <span>הזמנה {file.orderCode} · {formatDate(file.createdAt)}</span>
                    <small>{file.mimeType} · {formatBytes(file.sizeBytes)} · גרסה {file.version}</small>
                  </div>
                  <div className="account-file-actions">
                    <a href={`/account/orders/${encodeURIComponent(file.orderId)}`}>צפייה בהזמנה</a>
                    <a href={file.downloadUrl}>הורדה</a>
                    <button
                      type="button"
                      disabled={!targetLineId || reusingFileId !== null}
                      onClick={() => void reuse(file)}
                    >
                      {reusingFileId === file.id ? "משייך…" : "השתמש שוב"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </AccountChrome>
  );
}
